import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getRentVsBuyFieldExplainers } from '@/content/field-explainers/rent-vs-buy';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateRentVsBuy, type RentVsBuyInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  parseContractState
} from './shared';

type Props = { route: RouteDefinition };

const defaults: RentVsBuyInput = {
  monthlyRent: 2000,
  rentIncrease: 3,
  rentersInsurance: 200,
  investmentReturn: 7,
  homePrice: 400000,
  downPayment: 80000,
  interestRate: 6.875,
  loanTermYears: 30,
  propertyTax: 4800,
  homeInsurance: 1800,
  maintenancePercent: 1,
  homeAppreciation: 3,
  closingCostsBuy: 12000,
  sellingCostsPercent: 9,
  timeHorizon: 7
};

const chartConfig = {
  rent: {
    label: 'Rent cost',
    color: 'var(--chart-1)'
  },
  buy: {
    label: 'Buy cost',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

export function RentVsBuyCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<RentVsBuyInput>(() => parseContractState('rentVsBuy', defaults));

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateRentVsBuy(state), [state]);

  const chartData = useMemo(
    () =>
      result.chart.labels.map((year, index) => ({
        year,
        rent: result.chart.rentCosts[index],
        buy: result.chart.buyCosts[index]
      })),
    [result.chart.buyCosts, result.chart.labels, result.chart.rentCosts]
  );

  const recommendationLabel = result.recommendation === 'rent' ? 'Rent' : 'Buy';

  const explainers = useMemo(() => {
    const entries = getRentVsBuyFieldExplainers({
      input: {
        monthlyRent: state.monthlyRent,
        rentIncrease: state.rentIncrease,
        rentersInsurance: state.rentersInsurance,
        investmentReturn: state.investmentReturn,
        homePrice: state.homePrice,
        downPayment: state.downPayment,
        interestRate: state.interestRate,
        loanTermYears: state.loanTermYears,
        propertyTax: state.propertyTax,
        homeInsurance: state.homeInsurance,
        maintenancePercent: state.maintenancePercent,
        homeAppreciation: state.homeAppreciation,
        closingCostsBuy: state.closingCostsBuy,
        sellingCostsPercent: state.sellingCostsPercent,
        timeHorizon: state.timeHorizon
      },
      result: {
        loanAmount: result.loanAmount,
        monthlyMortgage: result.monthlyMortgage,
        totalRentPaid: result.totalRentPaid,
        totalRentersInsurance: result.totalRentersInsurance,
        investmentGains: result.investmentGains,
        totalRentCost: result.totalRentCost,
        totalMortgagePayments: result.totalMortgagePayments,
        totalPropertyTax: result.totalPropertyTax,
        totalHomeInsurance: result.totalHomeInsurance,
        totalMaintenance: result.totalMaintenance,
        futureHomeValue: result.futureHomeValue,
        remainingLoanBalance: result.remainingLoanBalance,
        sellingCosts: result.sellingCosts,
        grossSaleValue: result.grossSaleValue,
        saleProceeds: result.saleProceeds,
        totalBuyCost: result.totalBuyCost,
        netBuyCost: result.netBuyCost,
        recommendation: result.recommendation,
        breakEvenYear: result.breakEvenYear
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result]);

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Rent vs Buy Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Compare long-term housing costs and identify when buying may outperform renting.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel title="Housing assumptions" description="Set rent, purchase, financing, and appreciation assumptions for side-by-side comparison.">
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="rvbMonthlyRent" label="Monthly rent" value={state.monthlyRent} min={0} step={25} prefix="$" explainer={explainers.byId.get('monthlyRent')} onChange={(value) => setState((prev) => ({ ...prev, monthlyRent: value }))} />
            <NumberField id="rvbRentIncrease" label="Annual rent increase" value={state.rentIncrease} min={0} max={10} step={0.1} suffix="%" explainer={explainers.byId.get('rentIncrease')} onChange={(value) => setState((prev) => ({ ...prev, rentIncrease: value }))} />
            <NumberField id="rvbRentInsurance" label="Renter insurance (annual)" value={state.rentersInsurance} min={0} step={25} prefix="$" explainer={explainers.byId.get('rentersInsurance')} onChange={(value) => setState((prev) => ({ ...prev, rentersInsurance: value }))} />
            <NumberField id="rvbInvestmentReturn" label="Investment return" value={state.investmentReturn} min={0} max={12} step={0.1} suffix="%" explainer={explainers.byId.get('investmentReturn')} onChange={(value) => setState((prev) => ({ ...prev, investmentReturn: value }))} />
            <NumberField id="rvbHomePrice" label="Home price" value={state.homePrice} min={0} step={1000} prefix="$" explainer={explainers.byId.get('homePrice')} onChange={(value) => setState((prev) => ({ ...prev, homePrice: value }))} />
            <NumberField id="rvbDownPayment" label="Down payment" value={state.downPayment} min={0} step={1000} prefix="$" explainer={explainers.byId.get('downPayment')} onChange={(value) => setState((prev) => ({ ...prev, downPayment: value }))} />
            <NumberField id="rvbInterestRate" label="Mortgage rate" value={state.interestRate} min={0} max={12} step={0.1} suffix="%" explainer={explainers.byId.get('interestRate')} onChange={(value) => setState((prev) => ({ ...prev, interestRate: value }))} />
            <NumberField id="rvbLoanTerm" label="Loan term (years)" value={state.loanTermYears} min={5} max={40} step={1} parseMode="integer" explainer={explainers.byId.get('loanTermYears')} onChange={(value) => setState((prev) => ({ ...prev, loanTermYears: Math.round(value) }))} />
            <NumberField id="rvbPropertyTax" label="Property tax (annual)" value={state.propertyTax} min={0} step={100} prefix="$" explainer={explainers.byId.get('propertyTax')} onChange={(value) => setState((prev) => ({ ...prev, propertyTax: value }))} />
            <NumberField id="rvbHomeInsurance" label="Home insurance (annual)" value={state.homeInsurance} min={0} step={100} prefix="$" explainer={explainers.byId.get('homeInsurance')} onChange={(value) => setState((prev) => ({ ...prev, homeInsurance: value }))} />
            <NumberField id="rvbMaintenance" label="Maintenance %" value={state.maintenancePercent} min={0} max={5} step={0.1} suffix="%" explainer={explainers.byId.get('maintenancePercent')} onChange={(value) => setState((prev) => ({ ...prev, maintenancePercent: value }))} />
            <NumberField id="rvbAppreciation" label="Home appreciation" value={state.homeAppreciation} min={-3} max={10} step={0.1} suffix="%" allowNegative explainer={explainers.byId.get('homeAppreciation')} onChange={(value) => setState((prev) => ({ ...prev, homeAppreciation: value }))} />
            <NumberField id="rvbClosing" label="Closing costs (buy)" value={state.closingCostsBuy} min={0} step={500} prefix="$" explainer={explainers.byId.get('closingCostsBuy')} onChange={(value) => setState((prev) => ({ ...prev, closingCostsBuy: value }))} />
            <NumberField id="rvbSelling" label="Selling costs %" value={state.sellingCostsPercent} min={0} max={12} step={0.1} suffix="%" explainer={explainers.byId.get('sellingCostsPercent')} onChange={(value) => setState((prev) => ({ ...prev, sellingCostsPercent: value }))} />
            <NumberField id="rvbHorizon" label="Time horizon (years)" value={state.timeHorizon} min={1} max={30} step={1} parseMode="integer" explainer={explainers.byId.get('timeHorizon')} onChange={(value) => setState((prev) => ({ ...prev, timeHorizon: Math.round(value) }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Recommendation" value={recommendationLabel} note={result.breakEvenYear ? `break-even around year ${result.breakEvenYear}` : 'no break-even within 30 years'} trend={result.recommendation === 'buy' ? 'up' : 'flat'} explainer={explainers.byId.get('recommendation')} />
          <ResultSummaryCard label="Total rent path cost" value={formatCurrency(result.totalRentCost)} note={`horizon: ${state.timeHorizon} years`} trend={result.recommendation === 'rent' ? 'up' : 'down'} explainer={explainers.byId.get('totalRentCost')} />
          <ResultSummaryCard label="Net buy path cost" value={formatCurrency(result.netBuyCost)} note="includes sale proceeds and selling costs" trend={result.recommendation === 'buy' ? 'up' : 'down'} explainer={explainers.byId.get('netBuyCost')} />
          <ResultSummaryCard label="Monthly mortgage" value={formatCurrency(result.monthlyMortgage)} note={`loan amount ${formatCurrency(result.loanAmount)}`} trend="flat" explainer={explainers.byId.get('monthlyMortgage')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'rentVsBuy', state as Record<string, unknown>);
              const ok = await copyToClipboard(shareUrl);
              toast({ title: ok ? 'Share link copied' : 'Share link ready', description: ok ? shareUrl : `Copy manually: ${shareUrl}`, variant: ok ? 'success' : 'warning' });
            }}
            onExportCsv={() => {
              const headers = ['Year', 'Rent Cost', 'Buy Cost'];
              const rows = chartData.map((row) => [row.year, Math.round(row.rent), Math.round(row.buy)]);
              exportRowsToCsv(headers, rows, 'rent-vs-buy-comparison.csv');
              toast({ title: 'CSV exported', description: 'rent-vs-buy-comparison.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Rent vs Buy Comparison',
                subtitle: `${state.timeHorizon} year horizon`,
                summaryLeft: [
                  `Monthly rent: ${formatCurrency(state.monthlyRent)}`,
                  `Home price: ${formatCurrency(state.homePrice)}`,
                  `Mortgage rate: ${state.interestRate.toFixed(2)}%`,
                  `Recommendation: ${recommendationLabel}`
                ],
                summaryRight: [
                  `Total rent cost: ${formatCurrency(result.totalRentCost)}`,
                  `Net buy cost: ${formatCurrency(result.netBuyCost)}`,
                  `Break-even year: ${result.breakEvenYear ?? 'N/A'}`,
                  `Monthly mortgage: ${formatCurrency(result.monthlyMortgage)}`
                ],
                headers: ['Year', 'Rent Cost', 'Buy Cost'],
                rows: chartData.map((row) => [row.year, formatCurrency(row.rent), formatCurrency(row.buy)]),
                filename: 'rent-vs-buy-comparison.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'rent-vs-buy-comparison.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Rent vs buy costs over time"
          description="Complete yearly cost curves for both paths."
          legend={<ChartLegendPills config={chartConfig} keys={['rent', 'buy']} />}
        >
          <ChartContainer config={chartConfig} family="comparison">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={70} tickFormatter={(value) => formatCompactCurrency(Number(value))} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Line dataKey="rent" type="natural" stroke="var(--color-rent)" strokeWidth={2.4} dot={false} />
              <Line dataKey="buy" type="natural" stroke="var(--color-buy)" strokeWidth={3.1} dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Yearly table" description="Complete yearly cost snapshots for both paths.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Year</TableHead>
                <TableHead numeric>Rent cost</TableHead>
                <TableHead numeric>Buy cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartData.map((row) => (
                <TableRow key={row.year}>
                  <TableCell numeric>{row.year}</TableCell>
                  <TableCell numeric>{formatCurrency(row.rent)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.buy)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, explained with your numbers filled in. Tap the info icon next to any field for the same explanation in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
